// Categories: built-in tree + default custom seeds.
// 模型：所有节点都"可应用"（hybrid 节点 = 既可点又有 children）。
// 没有 [G] group 类型——某个节点是否有子项由 CAT_TREE 的 children 字段决定。
//
// Note: CATEGORIES / CAT_BY_ID are MUTATED at runtime when the user adds custom
// categories. Consumers receive live bindings to the same array/object — push /
// splice / property assignment all propagate.

export const CATEGORIES = [
  { id: 'sleep',       label: '睡眠',         name: '睡眠',     short: '睡',   color: 'var(--c-sleep)',   block: true,  key: '1' },
  { id: 'prep',        label: '预备',         name: '预备',     short: '备',   color: 'var(--c-prep)',    block: true,  desc: '小能熊 / 怦然' },
  { id: 'prep_xnx',    label: '预备·小能熊',  name: '小能熊',   short: '能',   color: 'var(--c-prep1)',   block: true,  key: '2', desc: '时间规划' },
  { id: 'prep_pr',     label: '预备·怦然',    name: '怦然',     short: '怦',   color: 'var(--c-prep2)',   block: true,  key: '3', desc: '整理/清洁/做饭' },
  { id: 'exercise',    label: '运动健康',     name: '运动健康', short: '运',   color: 'var(--c-exer)',    block: false, key: '4' },
  { id: 'exer_yoga',   label: '运动·瑜伽',    name: '瑜伽',     short: '瑜',   color: 'var(--c-exer-yoga)', block: false, desc: '瑜伽/八段锦' },
  { id: 'exer_foot',   label: '运动·泡脚',    name: '泡脚',     short: '脚',   color: 'var(--c-exer-foot)', block: false, desc: '泡脚' },
  { id: 'exer_wash',   label: '运动·洗澡',    name: '洗澡',     short: '澡',   color: 'var(--c-exer-wash)', block: true,  desc: '洗澡' },
  { id: 'exer_hair',   label: '运动·洗头',    name: '洗头',     short: '头',   color: 'var(--c-exer-hair)', block: true,  desc: '洗头' },
  { id: 'focus',       label: '专注',         name: '专注',     short: '专',   color: 'var(--c-focus)',   block: true,  key: '5' },
  { id: 'focus_work',  label: '专注·求职',    name: '求职',     short: '职',   color: 'var(--c-focus)',   block: true,  desc: '求职/UX/产品/PMP' },
  { id: 'fun',         label: '娱乐放松',     name: '娱乐放松', short: '娱',   color: 'var(--c-fun)',     block: false, key: '7' },
  // 注：eat id 保持不变（兼容存量数据），只是树位置移到 fun 下、label 改"娱乐·吃"
  { id: 'eat',         label: '娱乐·吃',      name: '吃',       short: '吃',   color: 'var(--c-fun1)',    block: true,  key: '6' },
  { id: 'fun_s',       label: '娱乐·拉',      name: '拉',       short: '拉',   color: 'var(--c-fun2)',    block: false, desc: '拉屎/拉稀' }, /* 线框（与社交·帮忙对换样式，用户 2026-06-15）*/
  { id: 'fun_book',    label: '娱乐·书影音',  name: '书影音',   short: '书',   color: 'var(--c-fun3)',    block: false, desc: '阅读/看剧' },
  { id: 'fun_relax',   label: '娱乐·放松',    name: '放松',     short: '松',   color: 'var(--c-fun)',     block: false },
  { id: 'social',      label: '亲友社交',     name: '亲友社交', short: '亲',   color: 'var(--c-soc)',     block: false },
  { id: 'social_talk', label: '社交·互动',    name: '互动',     short: '互',   color: 'var(--c-soc3)',    block: false, desc: '吵架/博弈/互赞' },
];

export const CAT_BY_ID = Object.fromEntries(CATEGORIES.map(c => [c.id, c]));

// Hierarchical tree. Every node here is also in CATEGORIES (and thus applicable).
// 'children' is optional —— 有 children 的节点是 hybrid（可应用 + 有子项）。
export const CAT_TREE = [
  { id: 'sleep' },
  { id: 'prep',     children: ['prep_xnx', 'prep_pr'] },
  { id: 'exercise', children: ['exer_yoga', 'exer_foot', 'exer_wash', 'exer_hair'] },
  { id: 'focus',    children: ['focus_work'] },
  { id: 'fun',      children: ['eat', 'fun_s', 'fun_book', 'fun_relax'] },
  { id: 'social',   children: ['social_talk'] },
];

// 默认自定义子分类（[S] 种子）。
// 新设备 / 新浏览器首次启动时，自动种入 customCats。
// 已有 customCats 的设备不会被覆盖。用户在 UI 删除后不会复活。
export const DEFAULT_CUSTOM_CATS = [
  { id: 'sleep_lazy',  parentId: 'sleep',    label: '睡眠·赖床', name: '赖床', short: '赖', color: 'var(--c-sleep-lazy)', block: true,  desc: '赖床/卧床' },
  // 躺着没睡：晚上躺床上没睡着（玩手机/发呆/拖延）。名字刻意避开「赖床/卧床」，避免被 dashboard
  // 的赖床正则吞掉、也不会被 isAsleep 当成睡着（不污染入睡时间）；单独按「入睡拖延」关联统计。
  // dashboard 识别锚定 id 'sleep_phone'（保留旧 id 兼容存量数据），改名不会失效。
  { id: 'sleep_phone', parentId: 'sleep',    label: '睡眠·躺着没睡', name: '躺着没睡', short: '躺', color: 'var(--c-sleep-phone)', block: true, desc: '睡前躺床上没睡着（玩手机/发呆/拖延）' },
  { id: 'exer_run',    parentId: 'exercise', label: '运动·跑步', name: '跑步', short: '跑', color: 'var(--c-exer-run)',   block: false },
  { id: 'focus_en',    parentId: 'focus',    label: '专注·英语', name: '英语', short: '英', color: 'var(--c-focus1)',     block: false, desc: '英语/日语/泰语' },
  { id: 'focus_py',    parentId: 'focus',    label: '专注·数据', name: '数据', short: '数', color: 'var(--c-focus2)', block: false, desc: '系统分析/数据分析/Excel' },
  { id: 'social_tra',  parentId: 'social',   label: '社交·交通', name: '交通', short: '交', color: 'var(--c-soc1)',   block: true,  desc: '在路上' },
  { id: 'social_help', parentId: 'social',   label: '社交·帮忙', name: '帮忙', short: '帮', color: 'var(--c-soc2)',   block: true,  desc: '帮忙干活/不求不助' }, /* 填充（原线框太淡，与娱乐·拉对换样式，用户 2026-06-15）*/
];
